import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import EmptyState from './components/EmptyState';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const Portfolio = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    material: 'all',
    technique: 'all',
    industry: 'all'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isGridView, setIsGridView] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const projectsPerPage = 12;

  // Fabric portfolio data
  const portfolioProjects = [
    {
      id: 1,
      title: "Premium Polyester Fabric Collection",
      client: "Wholesale & Manufacturing",
      image: "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=400&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1509631179647-0177331693ae?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1558769132-cb1aea9c2b4d?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=600&fit=crop"
      ],
      description: `High-quality polyester fabrics perfect for fashion, upholstery, and industrial applications. Available in various weights, colors, and finishes. Durable, wrinkle-resistant, and easy-care properties make it ideal for both casual and formal wear.`,
      material: "polyester",
      technique: "digital",
      quantity: "100-150 meters",
      specifications: {
        "fabric-type": "100% Polyester",
        "MOQ": "100 - 150 meters",
        "weight-range": "50-300 GSM",
        "width": "44-60 inches",
        "finishing": "Multiple finishes available"
      },
      testimonial: "Outstanding polyester fabric quality with excellent color fastness and durability. Perfect for our fashion line!"
    },
    {
      id: 2,
      title: "Natural Crepe Fabric Range",
      client: "Wholesale & Manufacturing",
      image: "/assets/portfolio/natural-crape1.jpg",
      gallery: [
        "/assets/portfolio/natural-crape1.jpg",
        "/assets/portfolio/natural-crape2.jpg",
        "/assets/portfolio/natural-crape-3.jpg"
      ],
      description: `Premium natural crepe fabrics with a distinctive crinkled texture and elegant drape. Perfect for sarees, suits, dresses, and ethnic wear. Available in solid colors and printed designs with excellent breathability and comfort.`,
      material: "natural-crepe",
      technique: "digital",
      quantity: "100-150 meters",
      specifications: {
        "fabric-type": "Natural Crepe",
        "MOQ": "100 - 150 meters",
        "texture": "Crinkled finish",
        "weight": "80-150 GSM",
        "drape": "Excellent flow"
      },
      testimonial: "Beautiful natural crepe with amazing texture and drape. Our customers love the quality and comfort of this fabric!"
    },
    {
      id: 3,
      title: "Velvet Embroidery Fabric Collection",
      client: "Wholesale & Manufacturing",
      image: "/assets/portfolio/velvet-1.png",
      gallery: [
        "/assets/portfolio/velvet-1.png",
        "/assets/portfolio/velvet-2.png"
      ],
      description: `Luxurious velvet fabrics with exquisite embroidery work. Perfect for bridal wear, party wear, and premium ethnic garments. Features intricate patterns, rich colors, and premium quality embroidery threads for a stunning look.`,
      material: "velvet-embroidery",
      technique: "embroidery",
      quantity: "50-100 meters",
      specifications: {
        "fabric-base": "Premium Velvet",
        "MOQ": "50 - 100 meters",
        "embroidery": "Machine & Hand work",
        "thread-type": "Zari, Sequins, Beads",
        "occasions": "Bridal & Festive wear"
      },
      testimonial: "Absolutely stunning velvet embroidery! The richness and detail of the work is exceptional. Perfect for our premium collection."
    },
    {
      id: 4,
      title: "Chinon Fabric Collection",
      client: "Wholesale & Manufacturing",
      image: "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=400&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=800&h=600&fit=crop"
      ],
      description: `Delicate and lightweight Chinon fabric with a soft, smooth texture and elegant drape. Ideal for sarees, dupattas, scarves, and flowing dresses. Available in vibrant colors with excellent printability for both digital and traditional designs.`,
      material: "chinon",
      technique: "digital",
      quantity: "50-100 meters",
      specifications: {
        "fabric-type": "Chinon Chiffon",
        "MOQ": "50 - 100 meters",
        "weight": "45-80 GSM",
        "texture": "Soft & Smooth",
        "transparency": "Semi-transparent"
      },
      testimonial: "Beautiful Chinon fabric with amazing flow and drape. Perfect for our ethnic wear collection. Customers love the quality!"
    },
    {
      id: 6,
      title: "Shimmer Fabric Collection",
      client: "Wholesale & Manufacturing",
      image: "https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=600&h=400&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1617038260897-41a1f14a8ca0?w=800&h=600&fit=crop"
      ],
      description: `Glamorous shimmer fabrics with a lustrous, eye-catching finish. Perfect for party wear, festive occasions, and evening gowns. Features metallic sheen, excellent drape, and available in a wide range of colors for that perfect sparkle.`,
      material: "shimmer",
      technique: "digital",
      quantity: "100-150 meters",
      specifications: {
        "fabric-type": "Shimmer Georgette",
        "MOQ": "100 - 150 meters",
        "finish": "Metallic Sheen",
        "weight": "60-120 GSM",
        "occasions": "Party & Festive wear"
      },
      testimonial: "Stunning shimmer fabric with beautiful shine and drape. Perfect for our party wear collection. Quality is consistently excellent!"
    }
  ];

  // Filter and search logic
  const filteredProjects = useMemo(() => {
    let filtered = portfolioProjects;

    // Apply filters
    if (filters?.material !== 'all') {
      filtered = filtered?.filter(project => project?.material === filters?.material);
    }
    if (filters?.technique !== 'all') {
      filtered = filtered?.filter(project => project?.technique === filters?.technique);
    }
    if (filters?.industry !== 'all') {
      filtered = filtered?.filter(project => project?.industry === filters?.industry);
    }

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(project =>
        project?.title?.toLowerCase()?.includes(searchLower) ||
        project?.client?.toLowerCase()?.includes(searchLower) ||
        project?.description?.toLowerCase()?.includes(searchLower) ||
        project?.material?.toLowerCase()?.includes(searchLower) ||
        project?.technique?.toLowerCase()?.includes(searchLower) ||
        project?.industry?.toLowerCase()?.includes(searchLower)
      );
    }

    return filtered;
  }, [filters, searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredProjects?.length / projectsPerPage);
  const paginatedProjects = filteredProjects?.slice(
    (currentPage - 1) * projectsPerPage,
    currentPage * projectsPerPage
  );

  // Handlers
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      material: 'all',
      technique: 'all',
      industry: 'all'
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleViewToggle = (gridView) => {
    setIsGridView(gridView);
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleNextProject = () => {
    if (selectedProject) {
      const currentIndex = filteredProjects?.findIndex(p => p?.id === selectedProject?.id);
      const nextIndex = (currentIndex + 1) % filteredProjects?.length;
      setSelectedProject(filteredProjects?.[nextIndex]);
    }
  };

  const handlePreviousProject = () => {
    if (selectedProject) {
      const currentIndex = filteredProjects?.findIndex(p => p?.id === selectedProject?.id);
      const prevIndex = currentIndex === 0 ? filteredProjects?.length - 1 : currentIndex - 1;
      setSelectedProject(filteredProjects?.[prevIndex]);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = Object.values(filters)?.some(value => value !== 'all') || searchTerm;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Fabric Portfolio - Kunjvihari Impex | Premium Fabric Manufacturer & Wholesaler</title>
        <meta name="description" content="Explore our premium fabric collection including Polyester, Natural Crepe, Velvet Embroidery, Chinon, and Shimmer fabrics. Wholesale and manufacturing services in Surat, Gujarat." />
        <meta name="keywords" content="polyester fabric, natural crepe, velvet embroidery fabric, chinon fabric, shimmer fabric, fabric wholesale Surat, textile manufacturer Gujarat, fabric supplier India" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-16 lg:pt-20 pb-8 lg:pb-12 bg-gradient-heritage text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-poppins mb-3 lg:mb-4">
                Our Premium Fabric Collection
              </h1>
              <p className="text-base lg:text-xl opacity-90 max-w-3xl mx-auto mb-6 lg:mb-8">
                Explore our extensive range of premium fabrics - from luxurious Polyester and elegant Natural Crepe 
                to stunning Velvet Embroidery, delicate Chinon, and glamorous Shimmer fabrics. 
                As manufacturers and wholesalers, we deliver quality and style in every thread.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-6 text-xs lg:text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="Award" size={16} />
                  <span>Premium Quality Fabrics</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={16} />
                  <span>Wholesale & Manufacturing</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="Star" size={16} />
                  <span>Wide Variety Available</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Results Section */}
          {filteredProjects?.length === 0 ? (
            <EmptyState 
              onClearFilters={handleClearFilters}
              hasFilters={hasActiveFilters}
              searchTerm={searchTerm}
            />
          ) : (
            <>
              {/* Results Header */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 lg:mb-6 gap-3">
                <div className="flex items-center gap-2 text-sm lg:text-base text-text-secondary">
                  <Icon name="Grid3X3" size={16} />
                  <span>
                    Showing {((currentPage - 1) * projectsPerPage) + 1}-{Math.min(currentPage * projectsPerPage, filteredProjects?.length)} of {filteredProjects?.length} fabrics
                  </span>
                </div>
                
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    iconName="X"
                    iconPosition="left"
                    iconSize={14}
                  >
                    Clear All
                  </Button>
                )}
              </div>

              {/* Projects Grid/List */}
              <div className={`${
                isGridView 
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6' :'space-y-4 lg:space-y-6'
              } mb-8 lg:mb-12`}>
                {paginatedProjects?.map((project) => (
                  <ProjectCard
                    key={project?.id}
                    project={project}
                    onViewDetails={handleViewDetails}
                    isListView={!isGridView}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    iconName="ChevronLeft"
                    iconSize={16}
                  />
                  
                  {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className="min-w-[40px]"
                    >
                      {page}
                    </Button>
                  ))}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    iconName="ChevronRight"
                    iconSize={16}
                  />
                </div>
              )}
            </>
          )}
        </main>

        {/* Call to Action Section */}
        <section className="bg-surface py-12 lg:py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-3 lg:mb-4">
              Ready to Place Your Order?
            </h2>
            <p className="text-text-secondary mb-6 lg:mb-8 text-base lg:text-lg">
              Contact us for wholesale pricing and minimum order quantities. We offer competitive rates for bulk orders.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="MessageSquare"
                iconPosition="left"
                iconSize={20}
                className="btn-textile"
                onClick={() => navigate('/contact')}
              >
                Get Wholesale Quote
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                iconSize={20}
                onClick={() => window.location.href = 'tel:+919974056700'}
              >
                Call: +91 99740 56700
              </Button>
            </div>
          </div>
        </section>

        {/* Project Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onNext={filteredProjects?.length > 1 ? handleNextProject : null}
          onPrevious={filteredProjects?.length > 1 ? handlePreviousProject : null}
        />

        {/* Footer */}
        <footer className="bg-white text-gray-900 py-6 sm:py-8 lg:py-12 border-t-2 border-primary/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
              {/* Company Info */}
              <div>
                <div className="flex items-center mb-3 sm:mb-4 lg:mb-6">
                  <img 
                    src="/assets/images/logo.png" 
                    alt="Kunjvihari Impex" 
                    className="h-[120px] sm:h-[136px] lg:h-[152px] w-auto" 
                  />
                </div>
                <p className="text-xs sm:text-sm text-gray-600">
                  <span className="font-semibold">GST:</span> 24ABCDE1234F1Z5
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-poppins font-semibold text-primary mb-2 sm:mb-3 lg:mb-4">
                  Quick Links
                </h3>
                <ul className="space-y-1.5 sm:space-y-2">
                  <li>
                    <a href="/" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Home
                    </a>
                  </li>
                  <li>
                    <a href="/about" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Portfolio
                    </a>
                  </li>
                  <li>
                    <a href="/contact" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>

              {/* Our Fabrics */}
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-poppins font-semibold text-primary mb-2 sm:mb-3 lg:mb-4">
                  Our Fabrics
                </h3>
                <ul className="space-y-1.5 sm:space-y-2">
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Position Print Fabric
                    </a>
                  </li>
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Polyester Fabric
                    </a>
                  </li>
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Natural Crepe
                    </a>
                  </li>
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Velvet Embroidery
                    </a>
                  </li>
                  <li>
                    <a href="/portfolio" className="text-xs sm:text-sm lg:text-base text-gray-600 hover:text-primary transition-colors font-medium">
                      Chinon & Shimmer
                    </a>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-sm sm:text-base lg:text-lg font-poppins font-semibold text-primary mb-2 sm:mb-3 lg:mb-4">
                  Contact Us
                </h3>
                <div className="space-y-1.5 sm:space-y-2 lg:space-y-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-primary mt-0.5">📍</span>
                    <span className="text-[10px] sm:text-xs lg:text-sm text-gray-600 leading-relaxed">
                      115, 116 Shree Darshan Industry Estate, Near Amrut Udhyog Nagar, Kamrej Road, Surat - 395010, Gujarat
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-primary">📞</span>
                    <a 
                      href="tel:+919974056700" 
                      className="text-xs sm:text-sm text-gray-600 hover:text-primary transition-colors font-medium"
                    >
                      +91 99740 56700
                    </a>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-primary">✉️</span>
                    <a 
                      href="mailto:info@kunjvihariimpex.com" 
                      className="text-xs sm:text-sm text-gray-600 hover:text-primary transition-colors font-medium"
                    >
                      info@kunjvihariimpex.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-200 pt-4 sm:pt-6 lg:pt-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-2 sm:gap-4">
                <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500 text-center md:text-left">
                  &copy; {new Date()?.getFullYear()} Kunjvihari Impex. All rights reserved.
                </p>
                <p className="text-[10px] sm:text-xs lg:text-sm text-gray-500 font-medium">
                  Precision in Every Thread
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Portfolio;